/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-10-30 12:14:19                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 13:57:01                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * Services
 * Components
 * Sections
 * Styles
 * Customers - customers view
 * useEffect - get customers list on init
 * useEffect - paginate list
 * handleCustomerCreate - handle customer creation event
 */

/* Imports */
import { t } from "i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
/***/

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";
import {
  SearchOptions,
  SearchQuery,
  searchWithFilters,
  toSearchOrder,
} from "../../services/api/search";
/***/

/* Hooks */
import { useCustomersFilters } from "../../hooks/filters";
/***/

/* Components */
import { Pagination } from "../../components/pagination/Pagination";
import CustomerForm, {
  CustomerFormValidator,
} from "../../components/data/customer/CustomerForm";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
/***/

/* Sections */
import CustomersTable from "./sections/CustomersTable";
import CustomerFilterForm, { getQueriesFromFilter } from "./filter/CustomerFilterForm";
/***/

/* Styles */
import "./Customers.scss";
import { getCustomerExportDescriptor } from "../../services/export/data/customerExport";
import ExportButton from "../../components/export-button/ExportButton";
import { useTablePageSize } from "../../hooks/useTablePageSize";
/***/

/**
 * Customers - customers view
 * display list of customers
 */
export function Customers() {
  const [customersPageState, setCustomersPageState] = useState<EasyTech.Customer[]>([]);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "name", orderDesc: false });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
  };

  // Fonction pour réinitialiser la page
  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  // Utilisation du hook de filtres
  const {
    filters,
    handleFilterSubmit,
    clearFilter: handleFilterClear,
    clearAllFilters,
    displayFilters
  } = useCustomersFilters(resetPage);

  /**
   * Charge les customers avec les filtres
   */
  /** Le tri affiche. Une seule definition, pour que l'export ne puisse pas diverger de la table. */
  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  /**
   * Le descripteur dépend du realm : la colonne Odoo n'apparaît que là où Odoo est branché.
   * Mémorisé pour garder une identité stable, dont la modale se sert pour initialiser la sélection
   * de colonnes.
   */
  const exportDescriptor = useMemo(() => getCustomerExportDescriptor(), []);

  const loadCustomersWithFilters = useCallback(async () => {
    if (!filters) return;

    setIsLoading(true);
    try {
      const queries: SearchQuery[] = getQueriesFromFilter(filters);

      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries,
      };

      const response = await searchWithFilters<EasyTech.Customer>(
        "customers",
        searchOptions
      );

      if (response && response.data) {
        setCustomersPageState(response.data);
        setPageNumberState(response.totalPages);
      } else {
        setCustomersPageState([]);
        setPageNumberState(1);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des customers avec filtres:", error);
      setCustomersPageState([]);
      setPageNumberState(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize]);

  useEffect(() => {
    loadCustomersWithFilters();
  }, [loadCustomersWithFilters]);


  /**
   * handleCustomerCreate - handle customer creation event
   * @param data - new customer data
   * @return - promise on finish
   */
  const handleCustomerCreate = (data: EasyTech.Customer): Promise<void> => {
    data = data as EasyTech.Customer;

    return new Promise((resolve, reject) => {
      if (validator.isFormValid(data, CustomerFormValidator)) {
        api
          .post("users/customers", data)
          .then((ret) => {
            toast.success(t("common:customerCreated"));
            return resolve();
          })
          .catch((err) => {
            showApiErrorToast(err);
            return reject(err);
          });
      } else {
        const fieldNames = {
          name: t("common:name"),
          activitySector: t("common:activitySector"),
          languages: t("common:languages"),
          nip: t("common:nip"),
          badPayer: t("common:badPayer"),
          contactInfo: t("common:contactInfo"),
          address: t("common:address")
        };

        const errorMessage = validator.getFormErrorMessage(data, CustomerFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };
  /***/

  const headerActions = (
    <>
      <ExportButton<EasyTech.Customer> filters={filters} entityName="customers"
        fileName="customers.xlsx"
        descriptor={exportDescriptor} getQueryFromFilter={getQueriesFromFilter}
        order={searchOrder}
      />

      <EditCreateDrawer<EasyTech.Customer>
        FormComponent={CustomerForm}
        isNotEditable={true}
        onSubmit={handleCustomerCreate}
        entityName="customer"
        validator={CustomerFormValidator}
        fieldNames={{
          name: t("common:name"),
          activitySector: t("common:activitySector"),
          languages: t("common:languages"),
          nip: t("common:nip"),
          badPayer: t("common:badPayer"),
          contactInfo: t("common:contactInfo"),
          address: t("common:address")
        }}
      />
    </>
  );

  const filterDrawerComponent = (
    <FilterDrawer<EasyTech.CustomerFilterType>
      FormComponent={CustomerFilterForm}
      onSubmit={handleFilterSubmit}
      entityName="Customers"
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={displayFilters}
      onClearFilter={handleFilterClear}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
      entity="customer"
    />
  );

  const mainContent =
    customersPageState && customersPageState.length > 0 ? (
      <CustomersTable customers={customersPageState} onHeadClick={onOrderChange}
        selectedHead={orderState.head} orderDesc={orderState.orderDesc} />
    ) : (
      <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
        <p className="text-lg text-blue-gray-700">{t("client:noData")}</p>
        <p className="text-sm text-blue-gray-500 mt-2">
          {filters && Object.keys(filters).length > 0
            ? t("client:noData:filters")
            : t("client:noData:add")}
        </p>
      </div>
    );

  const paginationComponent =
    customersPageState && customersPageState.length > 0 ? (
      <Pagination
        actualPage={pageState}
        pageNumber={pageNumberState}
        changePage={setPageState}
      />
    ) : null;

  return (
    <GenericListLayout
      entityName={t("common:customers")}
      path="/customers"
      isLoading={isLoading}
      headerActions={headerActions}
      filterDrawer={filterDrawerComponent}
      filterBadges={filterBadgesComponent}
      content={mainContent}
      pagination={paginationComponent}
      className="customers"
    />
  );
}
/***/
